import {
  ActionMenu,
  ConfirmationDialog,
  CopyableText,
  DataGrid,
  Dialog,
  Form,
  formatBytes,
  Input,
  useNotifications
} from '@ijoin/components';
import {
  getDownloadUrl,
  listObjects,
  useCreateFolder,
  useDeleteObject,
  useGetSettings,
  useMoveObject,
  useUploadObject
} from '@ijoin/hooks';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Breadcrumbs,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate
} from '@tanstack/react-router';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent
} from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';

type Row = {
  id: string;
  isFolder: boolean;
  key: string;
  lastModified?: string;
  name: string;
  size?: number;
};

const ObjectBrowserPage = () => {
  'use no memo';

  const routeParams = Route.useParams();
  const routeSearch = Route.useSearch();
  const navigate = useNavigate();
  const notifications = useNotifications();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [deleteTarget, setDeleteTarget] = useState<null | Row>(null);
  const [moveTarget, setMoveTarget] = useState<null | Row>(null);
  const [isCreateFolderOpen, toggleCreateFolderOpen] = useToggle();
  const folderMethods = useForm({ defaultValues: { folderName: '' } });
  const moveMethods = useForm({
    defaultValues: { dstBucket: '', dstKey: '', source: '' }
  });

  const s3Settings = useGetSettings();

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchIdRef = useRef(0);

  const fetchAllObjects = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;

    setRows([]);
    setLoading(true);

    const data = await listObjects(routeParams.bucket, {
      delimiter: '/',
      prefix: routeSearch.prefix || undefined
    });

    if (fetchId !== fetchIdRef.current) return;

    setRows([
      ...(data.commonPrefixes ?? [])
        .filter(cp => cp.prefix)
        .map(cp => {
          const parts = cp.prefix.replace(/\/$/, '').split('/');

          return {
            id: cp.prefix,
            isFolder: true,
            key: cp.prefix,
            name: parts[parts.length - 1] + '/'
          };
        }),
      ...(data.objects ?? [])
        .filter(obj => obj.key)
        .map(obj => {
          const parts = obj.key.split('/');

          return {
            id: obj.key,
            isFolder: false,
            key: obj.key,
            lastModified: obj.lastModified,
            name: parts[parts.length - 1],
            size: obj.size
          };
        })
    ]);
    setLoading(false);
  }, [routeParams.bucket, routeSearch.prefix]);

  useEffect(() => {
    fetchAllObjects();
  }, [fetchAllObjects]);

  const invalidate = () => fetchAllObjects();

  const uploadObject = useUploadObject({
    mutation: {
      onError: () => {
        notifications.error('Failed to upload file');
      },
      onSuccess: () => {
        invalidate();
        notifications.success('File uploaded');
      }
    }
  });

  const createFolder = useCreateFolder({
    mutation: {
      onError: () => {
        notifications.error('Failed to create folder');
      },
      onSuccess: () => {
        invalidate();
        notifications.success('Folder created');
        toggleCreateFolderOpen();
        folderMethods.reset();
      }
    }
  });

  const deleteObjectMutation = useDeleteObject({
    mutation: {
      onError: () => {
        notifications.error('Failed to delete');
      },
      onSuccess: () => {
        invalidate();
        notifications.success(`"${deleteTarget?.name}" deleted`);
        setDeleteTarget(null);
      }
    }
  });

  const moveObjectMutation = useMoveObject({
    mutation: {
      onError: () => {
        notifications.error('Failed to move object');
      },
      onSuccess: () => {
        invalidate();
        notifications.success(`"${moveTarget?.name}" moved`);
        setMoveTarget(null);
        moveMethods.reset();
      }
    }
  });

  const onDownload = useCallback(
    async (key: string) => {
      try {
        window.open(
          (await getDownloadUrl(routeParams.bucket, { key })).url,
          '_blank'
        );
      } catch {
        notifications.error('Failed to get download URL');
      }
    },
    [routeParams.bucket, notifications]
  );

  const onFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      const content = (reader.result as string).split(',')[1];

      uploadObject.mutate({
        bucket: routeParams.bucket,
        data: {
          content,
          contentType: file.type || 'application/octet-stream',
          fileName: file?.name,
          prefix: routeSearch.prefix || undefined
        }
      });
    };
    reader.readAsDataURL(file);
  };

  const onCloseCreateFolder = () => {
    toggleCreateFolderOpen();
    folderMethods.reset();
  };

  const onCreateFolderSubmit = () =>
    createFolder.mutate({
      bucket: routeParams.bucket,
      data: {
        key: `${routeSearch.prefix}${folderMethods.getValues().folderName}`
      }
    });

  const onMoveSubmit = () => {
    const values = moveMethods.getValues();

    moveObjectMutation.mutate({
      data: {
        destinationBucket: values.dstBucket,
        destinationKey: values.dstKey,
        sourceBucket: routeParams.bucket,
        sourceKey: moveTarget!.key
      }
    });
  };

  const onDeleteConfirm = () => {
    if (!deleteTarget) return;

    deleteObjectMutation.mutate({
      bucket: routeParams.bucket,
      data: { key: deleteTarget.key }
    });
  };

  const isAssetsBucket = routeParams.bucket === s3Settings.data?.assetsBucket;
  const prefixSegments = routeSearch.prefix.split('/').filter(Boolean);

  const columns = useMemo<GridColDef<Row>[]>(
    () => [
      {
        field: 'name',
        flex: 2,
        headerName: 'Name',
        renderCell: params =>
          params.row.isFolder ? (
            <Stack
              alignItems='center'
              direction='row'
              height='100%'
              spacing={0.5}>
              <Button
                onClick={() =>
                  navigate({
                    params: { bucket: routeParams.bucket },
                    search: { prefix: params.row.key },
                    to: '/s3-browser/$bucket'
                  })
                }
                startIcon={<FolderIcon />}
                sx={{ textTransform: 'none' }}
                variant='text'>
                {params.row.name}
              </Button>
              <CopyableText value={params.row.name} />
            </Stack>
          ) : (
            <Stack
              alignItems='center'
              direction='row'
              height='100%'
              spacing={0.5}>
              <Button
                onClick={() => onDownload(params.row.key)}
                startIcon={<InsertDriveFileIcon />}
                sx={{ textTransform: 'none' }}
                variant='text'>
                {params.row.name}
              </Button>
              <CopyableText
                value={
                  isAssetsBucket
                    ? `${s3Settings.data!.participantUrl}/assets/${params.row.key}`
                    : params.row.name
                }
              />
            </Stack>
          )
      },
      {
        align: 'right',
        field: 'size',
        flex: 0.5,
        headerAlign: 'right',
        headerName: 'Size',
        valueFormatter: value => formatBytes(value)
      },
      {
        field: 'lastModified',
        flex: 1,
        headerName: 'Last Modified',
        valueFormatter: value =>
          value ? new Date(value).toLocaleString() : '-'
      },
      {
        field: 'actions',
        headerName: '',
        renderCell: params => (
          <ActionMenu
            actions={[
              {
                content: 'Delete',
                onSelect: () => setDeleteTarget(params.row)
              },
              ...(!params.row.isFolder
                ? [
                    {
                      content: 'Move',
                      onSelect: () => {
                        setMoveTarget(params.row);
                        moveMethods.reset({
                          dstBucket: routeParams.bucket,
                          dstKey: params.row.key,
                          source: `${routeParams.bucket}/${params.row.key}`
                        });
                      }
                    }
                  ]
                : [])
            ]}
            trigger={
              <IconButton data-testid={`actions-${params.row.name}`}>
                <MoreVertIcon />
              </IconButton>
            }
          />
        ),
        sortable: false,
        width: 65
      }
    ],
    [isAssetsBucket, onDownload, navigate, routeParams.bucket, s3Settings.data]
  );

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Breadcrumbs>
        <Link
          style={{ color: 'inherit', textDecoration: 'none' }}
          to='/s3-browser'>
          S3 Buckets
        </Link>
        {prefixSegments.length === 0 ? (
          <Typography color='text.primary'>{routeParams.bucket}</Typography>
        ) : (
          <Link
            params={{ bucket: routeParams.bucket }}
            search={{ prefix: '' }}
            style={{ color: 'inherit', textDecoration: 'none' }}
            to='/s3-browser/$bucket'>
            {routeParams.bucket}
          </Link>
        )}
        {prefixSegments.map((seg, i) => {
          const segPrefix = prefixSegments.slice(0, i + 1).join('/') + '/';

          return i === prefixSegments.length - 1 ? (
            <Typography color='text.primary' key={segPrefix}>
              {seg}
            </Typography>
          ) : (
            <Link
              key={segPrefix}
              params={{ bucket: routeParams.bucket }}
              search={{ prefix: segPrefix }}
              style={{ color: 'inherit', textDecoration: 'none' }}
              to='/s3-browser/$bucket'>
              {seg}
            </Link>
          );
        })}
      </Breadcrumbs>

      <Stack direction='row' spacing={1}>
        <Button
          data-testid='uploadFile'
          onClick={() => fileInputRef.current?.click()}
          startIcon={<UploadFileIcon />}
          variant='contained'>
          Upload File
        </Button>
        <Button
          data-testid='createFolder'
          onClick={toggleCreateFolderOpen}
          startIcon={<CreateNewFolderIcon />}
          variant='outlined'>
          Create Folder
        </Button>
      </Stack>

      <input hidden onChange={onFileSelect} ref={fileInputRef} type='file' />

      <DataGrid
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] }
        }}
        loading={loading}
        pageSizeOptions={[25, 50, 100]}
        rows={rows}
        showColumnMenu
        slotProps={{
          root: { 'data-testid': 's3-objects' }
        }}
      />

      <Dialog
        data-testid='createFolderDialog'
        fullWidth
        isOpen={isCreateFolderOpen}
        maxWidth='xs'
        onClose={onCloseCreateFolder}>
        <Form
          methods={folderMethods}
          onSubmit={folderMethods.handleSubmit(onCreateFolderSubmit)}>
          <DialogTitle>Create Folder</DialogTitle>
          <DialogContent>
            <Input
              data-testid='folder-name'
              format='string'
              fullWidth
              label='Folder Name'
              name='folderName'
              size='small'
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelCreateFolder'
              onClick={onCloseCreateFolder}
              variant='outlined'>
              Cancel
            </Button>
            <Button
              data-testid='create-folder-submit'
              disabled={
                !folderMethods.watch('folderName') || createFolder.isPending
              }
              type='submit'
              variant='contained'>
              Create
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      <Dialog
        data-testid='moveObjectDialog'
        fullWidth
        isOpen={!!moveTarget}
        maxWidth='sm'
        onClose={() => setMoveTarget(null)}>
        <Form
          methods={moveMethods}
          onSubmit={moveMethods.handleSubmit(onMoveSubmit)}>
          <DialogTitle>Move Object</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Input
                disabled
                format='string'
                fullWidth
                label='Source'
                name='source'
                size='small'
              />
              <Input
                data-testid='move-dst-bucket'
                format='string'
                fullWidth
                label='Destination Bucket'
                name='dstBucket'
                size='small'
              />
              <Input
                data-testid='move-dst-key'
                format='string'
                fullWidth
                label='Destination Key'
                name='dstKey'
                size='small'
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelMove'
              onClick={() => setMoveTarget(null)}
              variant='outlined'>
              Cancel
            </Button>
            <Button
              data-testid='move-submit'
              disabled={
                !moveMethods.watch('dstBucket') ||
                !moveMethods.watch('dstKey') ||
                moveObjectMutation.isPending
              }
              type='submit'
              variant='contained'>
              Move
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      <ConfirmationDialog
        confirmationText='DELETE'
        confirmColor='error'
        confirmText='Delete'
        data-testid='delete-object-dialog'
        isOpen={!!deleteTarget}
        message={`Are you sure you want to delete "${deleteTarget?.name}"?${deleteTarget?.isFolder ? ' This only removes the folder marker.' : ''}`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={onDeleteConfirm}
        title='Delete Object'
      />
    </Stack>
  );
};

export const Route = createFileRoute('/s3-browser/$bucket')({
  beforeLoad: () => {
    if (localStorage.getItem('isAdmin') !== '1') {
      throw redirect({ to: '/organizations' });
    }
  },
  component: ObjectBrowserPage,
  validateSearch: (search: Record<string, unknown>) => ({
    prefix: (search.prefix as string) ?? ''
  })
});
